import axios from "axios";
import { getCookie } from "../utils/cookieUtils";

const withdrawApiBaseUrl = import.meta.env.VITE_WITHDRAW_API;

const withdrawApi = axios.create({
  baseURL: withdrawApiBaseUrl,
});

withdrawApi.interceptors.request.use((config) => {
  const token = getCookie("authToken");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// New interface for product quantity
export interface ProductQuantity {
  productId: string;
  quantity: number;
}

export interface CreateWithdrawRequest {
  amount: number;
  currency: string;
  giftTableID: string;
  // Updated to be an array of product quantities instead of string IDs.
  productsToExchange: ProductQuantity[];
}

export interface ValidateWithdrawMFARequest {
  withdrawID?: string;
  productExchangeID?: string;
  mfaCode: string;
}

export interface ConfirmWithdrawalRequest {
  withdrawID?: string;
  productExchangeID?: string;
}

export interface SetAccountNumberRequest {
  withdrawID: string;
  accountNumber: string;
}

export interface CreateWithdrawResponse {
  withdrawalID?: string;
  productExchangeID?: string;
}

export interface WithdrawResponse {
  id: string;
  userID: string;
  giftTableID: string;
  amount: number;
  accountNumber: string;
  currency: string;
  status: string;
  requestedAt: string;
  productExchangeId?: string;
  donationReceiptId?: string;
}

// Updated interface: Now the exchange response contains products (with productId and quantity).
export interface ProductExchangeResponse {
  id: string;
  userID: string;
  giftTableID: string;
  products: ProductQuantity[];
  adminNotes: string;
  status: string;
  requestedAt: string;
}

export interface SuccessWithdrawalsTotalResponse {
  total: number;
}

export interface SuccessWithdrawalsCountResponse {
  count: number;
}

export interface SetDonationReceiptIDRequest {
  withdrawID: string;
  donationReceiptID: string;
}

export interface ManualProfileRequest {
  withdrawID: string;
  bank?: string;
  account?: string;
  clabe?: string;
  accountType?: string;
  holderName?: string;
  contactName?: string;
  contactEmail?: string;
  contactPhone?: string;
  dateOfBirth?: string;
  rfc?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
}

export interface ManualProfileResponse {
  id: string;
  withdrawID: string;
  userID: string;
  bank?: string;
  account?: string;
  clabe?: string;
  accountType?: string;
  holderName?: string;
  contactName?: string;
  contactEmail?: string;
  contactPhone?: string;
  dateOfBirth?: string;
  rfc?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
}

export interface UpdateWithdrawStatusRequest {
  withdrawID: string;
  status: string;
}

export const createWithdrawRequest = async (
  withdrawRequest: CreateWithdrawRequest
): Promise<CreateWithdrawResponse> => {
  try {
    const response = await withdrawApi.post<CreateWithdrawResponse>(
      "/withdraw",
      withdrawRequest
    );
    return response.data;
  } catch (error) {
    console.error("Error creating withdrawal request", error);
    throw new Error("Failed to create withdrawal request");
  }
};

export const validateWithdrawMFA = async (
  mfaRequest: ValidateWithdrawMFARequest
): Promise<void> => {
  try {
    const response = await withdrawApi.patch("/validate-mfa", mfaRequest);
    if (response.status !== 200) {
      throw new Error("Failed to validate MFA code");
    }
  } catch (error) {
    console.error("Error validating MFA code", error);
    throw new Error("Failed to validate MFA code");
  }
};

export const confirmWithdrawal = async (
  confirmRequest: ConfirmWithdrawalRequest
): Promise<void> => {
  try {
    const response = await withdrawApi.patch(
      "/confirm-withdrawal",
      confirmRequest
    );
    if (response.status !== 200) {
      throw new Error("Failed to confirm withdrawal");
    }
  } catch (error) {
    console.error("Error confirming withdrawal", error);
    throw new Error("Failed to confirm withdrawal");
  }
};

export const getWithdrawalById = async (
  withdrawID: string
): Promise<WithdrawResponse> => {
  try {
    const response = await withdrawApi.get<WithdrawResponse | any>(`/withdraw`, {
      params: { withdrawID },
    });
    const d: any = response.data;
    return {
      ...d,
      userID: d.userID ?? d.userId,
      giftTableID: d.giftTableID ?? d.giftTableId,
    };
  } catch (error) {
    console.error("Error retrieving withdrawal", error);
    // Preserve Axios error for status inspection downstream.
    throw error;
  }
};

export const getProductExchangeById = async (
  exchangeID: string
): Promise<ProductExchangeResponse> => {
  try {
    const response = await withdrawApi.get<ProductExchangeResponse>(
      `/product-exchange`,
      {
        params: { exchangeID },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error retrieving product exchange", error);
    throw new Error("Failed to retrieve product exchange");
  }
};

export const listWithdrawals = async (
  userID: string,
  status?: string,
  giftTableId?: string
): Promise<WithdrawResponse[]> => {
  try {
    const params: Record<string, string> = { userId: userID };
    if (status) {
      params.status = status;
    }
    if (giftTableId) {
      params.giftTableId = giftTableId;
    }

    const response = await withdrawApi.get<WithdrawResponse[]>(
      `/list-withdrawals`,
      { params }
    );
    return response.data;
  } catch (error) {
    console.error("Error retrieving list of withdrawals", error);
    throw new Error("Failed to retrieve list of withdrawals");
  }
};

export const listProductExchanges = async (
  userID: string,
  status?: string,
  giftTableId?: string
): Promise<ProductExchangeResponse[]> => {
  try {
    const params: Record<string, string> = { userId: userID };
    if (status) {
      params.status = status;
    }
    if (giftTableId) {
      params.giftTableId = giftTableId;
    }

    const response = await withdrawApi.get<ProductExchangeResponse[]>(
      `/list-product-exchanges`,
      { params }
    );
    return response.data;
  } catch (error) {
    console.error("Error retrieving list of product exchanges", error);
    throw new Error("Failed to retrieve list of product exchanges");
  }
};

export const getTotalSuccessfulWithdrawalsByGiftTable = async (
  giftTableID: string
): Promise<SuccessWithdrawalsTotalResponse> => {
  try {
    const response = await withdrawApi.get<SuccessWithdrawalsTotalResponse>(
      "/withdrawals/successful-total",
      { params: { giftTableID } }
    );
    return response.data;
  } catch (error) {
    console.error("Error retrieving total successful withdrawals", error);
    throw new Error("Failed to retrieve total successful withdrawals");
  }
};

export const getSuccessfulWithdrawalsCountByGiftTable = async (
  giftTableID: string
): Promise<SuccessWithdrawalsCountResponse> => {
  try {
    const response = await withdrawApi.get<SuccessWithdrawalsCountResponse>(
      "/withdrawals/successful-count",
      { params: { giftTableID } }
    );
    return response.data;
  } catch (error) {
    console.error("Error retrieving withdrawal count", error);
    throw new Error("Failed to retrieve withdrawal count");
  }
};

export const setWithdrawAccountNumber = async (
  request: SetAccountNumberRequest
): Promise<{ message: string }> => {
  try {
    const response = await withdrawApi.put<{ message: string }>(
      "/withdraw/account-number",
      request
    );
    return response.data;
  } catch (error) {
    console.error("Error setting withdrawal account number", error);
    throw new Error("Failed to update withdrawal account number");
  }
};

export const setDonationReceiptID = async (
  request: SetDonationReceiptIDRequest
): Promise<{ message: string }> => {
  try {
    const response = await withdrawApi.put<{ message: string }>(
      "/withdraw/donation-receipt",
      request
    );
    return response.data;
  } catch (error) {
    console.error("Error setting donation receipt ID", error);
    throw new Error("Failed to update donation receipt ID");
  }
};

export const upsertManualProfile = async (
  payload: ManualProfileRequest
): Promise<ManualProfileResponse> => {
  try {
    const response = await withdrawApi.post<ManualProfileResponse>(
      "/withdraw/manual-profile",
      payload
    );
    return response.data;
  } catch (error) {
    console.error("Error saving manual profile", error);
    throw new Error("Failed to save manual profile");
  }
};

export const getManualProfile = async (
  withdrawID: string
): Promise<ManualProfileResponse | null> => {
  try {
    const response = await withdrawApi.get<ManualProfileResponse>(
      "/withdraw/manual-profile",
      {
        params: { withdrawID },
      }
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return null;
    }
    console.error("Error fetching manual profile", error);
    throw new Error("Failed to fetch manual profile");
  }
};

export const updateWithdrawStatus = async (
  payload: UpdateWithdrawStatusRequest
): Promise<{ message: string }> => {
  try {
    const response = await withdrawApi.patch<{ message: string }>(
      "/withdraw/status",
      payload
    );
    return response.data;
  } catch (error) {
    console.error("Error updating withdraw status", error);
    throw new Error("Failed to update withdraw status");
  }
};
